import './App.css';
import React, { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import Nav from './components/nav';
import Intro from './components/intro';
import Bio from './components/bio';
import Filmography from './components/filmography';
import Press from './components/press';
import Deniro from "./components/deniro";
import Footer from './components/footer';

gsap.registerPlugin(ScrollTrigger);

function App() {
  // State for active nav section
  const [activeSection, setActiveSection] = useState('');

  const bioSectionRef = useRef(null);
  const filmographySectionRef = useRef(null);
  const pressSectionRef = useRef(null);
  const deniroSectionRef = useRef(null);

  // Handle URL query parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const section = urlParams.get('section');

    if (section) {
      // Scroll to the specified section
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  // Update URL when section changes
  const updateURL = (section) => {
    const url = new URL(window.location);
    if (section) {
      url.searchParams.set('section', section);
    } else {
      url.searchParams.delete('section');
    }
    window.history.replaceState({}, '', url);
  };

  // Simple scroll position check for nav highlighting
  useEffect(() => {
    let lastUrlSection = null;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Default to 'about' so the highlight never goes blank over the intro
      let currentSection = 'about';

      if (scrollY >= 100) {
        if (scrollY + windowHeight >= documentHeight - 100) {
          // Near the bottom of the page
          currentSection = 'contact';
        } else {
          // Last section whose top has crossed the middle of the viewport
          const viewportMiddle = windowHeight / 2;
          for (const id of ['about', 'trailers', 'press']) {
            const element = document.getElementById(id);
            if (element) {
              if (element.getBoundingClientRect().top <= viewportMiddle) {
                currentSection = id;
              }
            }
          }
        }
      }

      setActiveSection(currentSection);

      // Only touch the URL when the section changes — Safari throws after
      // 100 history API calls per 30s, which permanently killed this listener.
      const urlSection = scrollY < 100 ? '' : currentSection;
      if (urlSection !== lastUrlSection) {
        lastUrlSection = urlSection;
        updateURL(urlSection);
      }
    };

    let ticking = false;
    const requestTick = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          ticking = false;
          handleScroll();
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });
    handleScroll(); // Initial call on mount

    return () => window.removeEventListener('scroll', requestTick);
  }, []);

  // Add scroll animations for fade in/up effect (mobile optimized)
  useEffect(() => {
    // ponytail: matchMedia replaces the old UA/deviceMemory heuristics
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobileDevice = window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;

    // Skip animations when the user prefers reduced motion
    if (prefersReducedMotion) {
      return;
    }

    const sections = [
      bioSectionRef.current,
      filmographySectionRef.current,
      pressSectionRef.current,
      deniroSectionRef.current,
    ];

    sections.forEach((section) => {
      if (section) {
        // Mobile-optimized animation settings
        const animationConfig = isMobileDevice ? {
          duration: 0.4, // Shorter duration
          ease: 'power2.out', // Simpler easing
          y: 20, // Less movement
        } : {
          duration: 1,
          ease: 'power3.out',
          y: 50,
        };

        gsap.fromTo(
          section,
          {
            opacity: 0,
            y: animationConfig.y,
          },
          {
            opacity: 1,
            y: 0,
            duration: animationConfig.duration,
            ease: animationConfig.ease,
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              end: 'top 50%',
              toggleActions: 'play none none none',
            },
          }
        );
      }
    });
  }, []);

  return (
    <div className="App">
      <div className="main">
        <Nav activeSection={activeSection} />
        <Intro />
        <div ref={bioSectionRef}>
          <Bio />
        </div>
        <div ref={filmographySectionRef}>
          <Filmography />
        </div>
        <div ref={pressSectionRef}>
          <Press />
        </div>
        <div ref={deniroSectionRef}>
          <Deniro />
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default App;
